import { useEffect, useRef, type ReactNode } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { LoadingState } from '@/components/common';
import { LoginScreen } from './LoginScreen';
import { setClientAuthToken } from '@/api/client';
import { BRANDING } from '@/lib/branding';

interface AuthGuardProps {
  children: ReactNode;
}

// Capture URL token synchronously on module load — before Zustand persist
// rehydration can race with React effects and overwrite it.
let _capturedUrlToken: string | null = null;
const params = new URLSearchParams(window.location.search);
const _urlToken = params.get('auth_token');
if (_urlToken) {
  _capturedUrlToken = _urlToken;
  // Clean URL immediately
  params.delete('auth_token');
  const cleanUrl = params.toString()
    ? `${window.location.pathname}?${params}`
    : window.location.pathname;
  window.history.replaceState({}, '', cleanUrl);
}

export function AuthGuard({ children }: AuthGuardProps) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isLoading = useAuthStore((state) => state.isLoading);
  const user = useAuthStore((state) => state.user);

  // Use refs to prevent duplicate fetches from StrictMode and hydration
  const hasFetchedUser = useRef(false);
  const hasFetchedTeams = useRef(false);

  // Track whether a fresh URL token is active so Zustand persist
  // rehydration cannot overwrite it with a stale value.
  const urlTokenActiveRef = useRef(!!_capturedUrlToken);

  // Sync persisted token to API client — but never let rehydration
  // overwrite a fresh URL token that was just captured.
  const persistedToken = useAuthStore((state) => state.token);
  useEffect(() => {
    if (urlTokenActiveRef.current) {
      // A fresh URL token is in play — ignore rehydrated/persisted values
      return;
    }
    setClientAuthToken(persistedToken);
  }, [persistedToken]);

  // Fetch user on mount
  useEffect(() => {
    if (hasFetchedUser.current) return;
    hasFetchedUser.current = true;

    if (_capturedUrlToken) {
      const freshToken = _capturedUrlToken;
      _capturedUrlToken = null;

      // Store token for Bearer auth (cross-domain cookies are blocked in modern browsers)
      useAuthStore.getState().setToken(freshToken);
      setClientAuthToken(freshToken);
      useAuthStore.getState().fetchCurrentUser().then(() => {
        // Token is now persisted in the store; safe to let future
        // rehydration/persisted-token changes flow through.
        urlTokenActiveRef.current = false;
      });
    } else {
      // Restore persisted token to API client
      const stored = useAuthStore.getState().token;
      if (stored) setClientAuthToken(stored);
      useAuthStore.getState().fetchCurrentUser();
    }
  }, []);

  // Fetch teams only after user is confirmed authenticated
  useEffect(() => {
    if (isAuthenticated && user && !hasFetchedTeams.current) {
      hasFetchedTeams.current = true;
      useAuthStore.getState().fetchTeams();
    }
  }, [isAuthenticated, user]);

  // Listen for unauthorized events
  useEffect(() => {
    const handleUnauthorized = () => {
      useAuthStore.getState().setToken(null);
      setClientAuthToken(null);
      useAuthStore.getState().reset();
      // Reset refs so next login will fetch fresh data
      hasFetchedUser.current = false;
      hasFetchedTeams.current = false;
    };

    window.addEventListener('auth:unauthorized', handleUnauthorized);
    return () => {
      window.removeEventListener('auth:unauthorized', handleUnauthorized);
    };
  }, []);

  if (isLoading) {
    return (
      <div className="app-loading">
        <LoadingState message={`Loading ${BRANDING.appName}...`} size="lg" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginScreen />;
  }

  return <>{children}</>;
}

export default AuthGuard;
